import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../shared/config";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { ShieldCheck, ShieldAlert, Copy, Check } from "lucide-react";

const MfaManager = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enroll, setEnroll] = useState(null); // {qr_data_url, secret}
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(null); // codes après activation
  const [copied, setCopied] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [pwd, setPwd] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/auth/mfa/status`);
      setStatus(r.data);
    } catch { /* silencieux */ } finally { setLoading(false); }
  };
  useEffect(() => { fetchStatus(); }, []);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/mfa/enroll/start`);
      setEnroll(r.data);
      setRecovery(null);
      setCode('');
      setEnrollOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'activation");
    } finally { setBusy(false); }
  };

  const confirmEnroll = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/mfa/enroll/confirm`, { code: code.trim() });
      setRecovery(r.data.recovery_codes || []);
      toast.success("Double authentification activée");
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code invalide");
    } finally { setBusy(false); }
  };

  const disableMfa = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/auth/mfa/disable`, { password: pwd });
      toast.success("Double authentification désactivée");
      setDisableOpen(false); setPwd('');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mot de passe incorrect");
    } finally { setBusy(false); }
  };

  const copyRecovery = () => {
    navigator.clipboard.writeText((recovery || []).join('\n'));
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const enabled = status?.enabled;

  return (
    <Card data-testid="mfa-manager">
      <CardHeader>
        <div className="flex items-center gap-2">
          {enabled ? <ShieldCheck className="w-5 h-5 text-emerald-600" /> : <ShieldAlert className="w-5 h-5 text-amber-500" />}
          <CardTitle>Double authentification (2FA)</CardTitle>
        </div>
        <CardDescription>
          Sécurité renforcée conforme RGPD (art. 32). Un code temporaire depuis votre téléphone (Google Authenticator, Authy…) est demandé à la connexion. Facultatif.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-gray-400">Chargement…</p>
        ) : enabled ? (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm">
              <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-700">
                <ShieldCheck className="w-4 h-4" /> Activée
              </span>
              <span className="text-gray-500 ml-3">{status.recovery_codes_restants} code(s) de récupération restant(s)</span>
            </div>
            <Button variant="outline" className="text-red-600 border-red-200" onClick={() => setDisableOpen(true)} data-testid="mfa-disable-open-btn">
              Désactiver
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-gray-500">La double authentification n'est pas activée sur votre compte.</p>
            <Button onClick={startEnroll} disabled={busy} data-testid="mfa-enable-btn"
              className="bg-[#1173D4] hover:bg-[#0E5FB0] text-white">
              <ShieldCheck className="w-4 h-4 mr-2" /> Activer
            </Button>
          </div>
        )}
      </CardContent>

      {/* Modale d'enrôlement */}
      <Dialog open={enrollOpen} onOpenChange={(v) => { if (!v) { setEnrollOpen(false); fetchStatus(); } }}>
        <DialogContent className="max-w-md" data-testid="mfa-enroll-modal">
          <DialogHeader>
            <DialogTitle>{recovery ? "Codes de récupération" : "Activer la double authentification"}</DialogTitle>
            <DialogDescription>
              {recovery
                ? "Conservez ces codes en lieu sûr. Chacun n'est utilisable qu'une seule fois, en cas de perte de votre téléphone."
                : "1. Scannez ce QR code avec votre application d'authentification. 2. Saisissez le code à 6 chiffres affiché."}
            </DialogDescription>
          </DialogHeader>

          {recovery ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg font-mono text-sm" data-testid="mfa-recovery-codes">
                {recovery.map((c, i) => <div key={i} className="text-center py-1">{c}</div>)}
              </div>
              <Button variant="outline" className="w-full" onClick={copyRecovery}>
                {copied ? <><Check className="w-4 h-4 mr-2 text-emerald-600" /> Copié</> : <><Copy className="w-4 h-4 mr-2" /> Copier les codes</>}
              </Button>
              <Button className="w-full bg-[#1173D4] text-white" onClick={() => { setEnrollOpen(false); fetchStatus(); }} data-testid="mfa-recovery-done-btn">
                J'ai sauvegardé mes codes
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {enroll?.qr_data_url && (
                <div className="flex justify-center">
                  <img src={enroll.qr_data_url} alt="QR code 2FA" className="w-44 h-44 border rounded-lg" data-testid="mfa-qr" />
                </div>
              )}
              {enroll?.secret && (
                <p className="text-center text-xs text-gray-500">
                  Clé manuelle : <span className="font-mono select-all">{enroll.secret}</span>
                </p>
              )}
              <div className="space-y-2">
                <Label>Code à 6 chiffres</Label>
                <Input inputMode="numeric" maxLength={6} value={code} placeholder="123456"
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-lg tracking-[0.3em]" data-testid="mfa-enroll-code-input" />
              </div>
              <Button className="w-full bg-[#1173D4] text-white" disabled={busy || code.length !== 6} onClick={confirmEnroll} data-testid="mfa-enroll-confirm-btn">
                {busy ? "Vérification…" : "Activer"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modale de désactivation */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent className="max-w-sm" data-testid="mfa-disable-modal">
          <DialogHeader>
            <DialogTitle>Désactiver la 2FA</DialogTitle>
            <DialogDescription>Confirmez avec votre mot de passe.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input type="password" placeholder="Mot de passe" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="mfa-disable-pwd-input" />
            <Button variant="destructive" className="w-full" disabled={busy || !pwd} onClick={disableMfa} data-testid="mfa-disable-confirm-btn">
              Désactiver
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MfaManager;
